import { useEffect, useState, useRef } from "react";
import "./App.css";
import { SubscriptionType } from "./types/SubscriptionType";
import Subscription from "./components/Subscription";
import SubscriptionForm from "./components/SubscriptionForm";
import SubscriptionListModal from "./components/SubscriptionListModal";

const LOCAL_STORAGE_KEY = "subscriptions";

type FormState = {
    card: string;
    device: string;
    name: string;
    type: "primary" | "secondary";
};

function getInitialFormState(): FormState {
    return {
        card: "",
        device: "",
        name: "",
        type: "primary",
    };
}

function App() {
    const [subscriptions, setSubscriptions] = useState<SubscriptionType[]>(
        []
    );
    const [form, setForm] = useState<FormState>(getInitialFormState());
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [showList, setShowList] = useState(false);

    // Load from localStorage and migrate from old qrConfigs if present
    useEffect(() => {
        // Migration from old qrConfigs
        const old = localStorage.getItem("qrConfigs");
        if (old) {
            try {
                const parsed = JSON.parse(old);
                if (Array.isArray(parsed)) {
                    const migrated = parsed
                        .map((item: any) => {
                            if (item.type === "dynamic") {
                                return {
                                    card: item.cardNumber,
                                    device: item.deviceId,
                                    name: item.label,
                                    type: "primary",
                                };
                            } else if (item.type === "static") {
                                return {
                                    card: item.staticText,
                                    name: item.label,
                                    type: "secondary",
                                };
                            }
                            return null;
                        })
                        .filter((x): x is SubscriptionType => x !== null);
                    // Merge with any existing subscriptions
                    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
                    let existing: SubscriptionType[] = [];
                    if (saved) {
                        existing = JSON.parse(saved);
                    }
                    const all = [...migrated, ...existing];
                    setSubscriptions(all);
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(all));
                }
            } catch (e) {
                // ignore parse errors
            }
            localStorage.removeItem("qrConfigs");
        } else {
            // Normal load
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (saved) {
                setSubscriptions(JSON.parse(saved));
            }
        }
    }, []);

    // Save to localStorage
    useEffect(() => {
        if (subscriptions.length === 0) {
            return;
        }
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(subscriptions));
    }, [subscriptions]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.card.trim() || !form.name.trim()) return;
        if (form.type === "primary" && !form.device.trim()) return;
        if (editIndex !== null) {
            setSubscriptions((subs) =>
                subs.map((sub, idx) =>
                    idx === editIndex
                        ? form.type === "primary"
                            ? {
                                  card: form.card,
                                  device: form.device,
                                  name: form.name,
                                  type: "primary",
                              }
                            : {
                                  card: form.card,
                                  name: form.name,
                                  type: "secondary",
                              }
                        : sub
                )
            );
            setEditIndex(null);
            closeForm();
        } else {
            setSubscriptions((subs) => [
                ...subs,
                form.type === "primary"
                    ? {
                          card: form.card,
                          device: form.device,
                          name: form.name,
                          type: "primary",
                      }
                    : { card: form.card, name: form.name, type: "secondary" },
            ]);
            closeForm();
        }
        setForm(getInitialFormState());
    };

    const handleEdit = (idx: number) => {
        const sub = subscriptions[idx];
        setForm({
            card: sub.card,
            device: sub.type === "primary" ? sub.device : "",
            name: sub.name,
            type: sub.type,
        });
        setEditIndex(idx);
        setShowForm(true);
        setShowList(false);
    };

    const clearLocalStorage = () => {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setSubscriptions([]);
    };

    const handleDelete = (idx: number) => {
        const subs = subscriptions.filter((_, i) => i !== idx);
        setSubscriptions(subs);
        if (subs.length === 0) {
            clearLocalStorage();
            setShowList(false);
        }

        if (editIndex === idx) {
            setEditIndex(null);
            setForm(getInitialFormState());
        }
    };

    const openForm = () => setShowForm(true);
    const closeForm = () => {
        setShowForm(false);
        setEditIndex(null);
        setForm(getInitialFormState());
    };
    const openList = () => setShowList(true);
    const closeList = () => setShowList(false);

    const handlePasteConfig = (subs: any[], onResult?: (success: boolean) => void) => {
        // Only accept objects with type 'primary' or 'secondary' and required fields
        const valid = subs.filter(
            (s) =>
                (s.type === "primary" && s.card && s.device && s.name) ||
                (s.type === "secondary" && s.card && s.name)
        );
        if (valid.length > 0) {
            setSubscriptions((old) => {
                const existingCards = new Set(old.map((s) => s.card));
                const unique = valid.filter((s) => !existingCards.has(s.card));
                if (unique.length > 0) {
                    if (onResult) onResult(true);
                    closeForm();
                    return [...old, ...unique];
                } else {
                    if (onResult) onResult(false);
                    return old;
                }
            });
        } else {
            if (onResult) onResult(false);
        }
    };

    return (
        <div className="App">
            <button
                style={{
                    position: "fixed",
                    top: "15px",
                    left: "15px",
                    zIndex: 1000,
                    backgroundColor: "white",
                    color: "black",
                    border: "none",
                    fontSize: "18px",
                    fontWeight: "bold",
                }}
                onClick={() => window.close()}
            >
                ✕
            </button>
            <header className="App-header">
                {subscriptions.map((subscription) => (
                    <div
                        key={subscription.card}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "10px",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100vh",
                        }}
                    >
                        <Subscription subscription={subscription} />
                    </div>
                ))}
            </header>

            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "15px",
                    backgroundColor: "white",
                }}
            >
                <button
                    style={{
                        width: "100%",
                        backgroundColor: "rgb(97, 49, 173)",
                        color: "white",
                        border: "none",
                        padding: "10px 30px",
                        borderRadius: "0px",
                        fontSize: "22px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: "bold",
                    }}
                    onClick={openForm}
                >
                    COMMENT UTILISER LE QR CODE
                </button>
                <button
                    onClick={openList}
                    style={{
                        width: "100%",
                        marginTop: "10px",
                        color: "rgb(254, 112, 0)",
                        backgroundColor: "white",
                        border: "none",
                        padding: "10px 30px",
                        borderRadius: "0px",
                        fontSize: "16px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: "bold",
                    }}
                >
                    Donne nous ton avis
                </button>
            </div>
            {showForm && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.3)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={closeForm}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "10px",
                            boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
                            minWidth: "320px",
                            maxWidth: "400px",
                            width: "90vw",
                            padding: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SubscriptionForm
                            form={form}
                            setForm={setForm}
                            onSubmit={handleSubmit}
                            onCancel={closeForm}
                            editIndex={editIndex}
                            onPasteConfig={(subs, onResult) => handlePasteConfig(subs, onResult)}
                        />
                    </div>
                </div>
            )}
            <SubscriptionListModal
                subscriptions={subscriptions}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                showList={showList}
                closeList={closeList}
            />
        </div>
    );
}

export default App;
